import { useState } from 'react';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  Input,
  Select,
  Table,
  Badge,
  Modal,
  ModalFooter,
  LoadingSpinner,
  ErrorSummary,
  EmptyState,
  ContentLoader,
} from '@/components/common';
import { useBulkSelection, BulkActionBar, BulkSelectCheckbox, BulkSelectAllCheckbox } from '@/components/bulk';
import { applyServerErrors } from '@/utils/formErrors';
import {
  useStates,
  useCreateState,
  useUpdateState,
  useDeleteState,
  useToggleStateActive,
  useCountryLookup,
  useCities,
} from '@/hooks/useGeography';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import type { City, State, StateRequest } from '@/services/geography';

const zoneOptions = [
  { value: '', label: 'All Zones' },
  { value: 'A', label: 'Zone A' },
  { value: 'B', label: 'Zone B' },
  { value: 'C', label: 'Zone C' },
];

const zoneColors: Record<string, 'primary' | 'success' | 'warning'> = {
  A: 'primary',
  B: 'success',
  C: 'warning',
};

export function StatesPage() {
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [countryFilter, setCountryFilter] = useState('');
  const [zoneFilter, setZoneFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingState, setEditingState] = useState<State | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  // Item 18: clicking a row opens a detail popup showing the cities linked to
  // that state. The row object itself (already fetched for the list) is
  // enough for the header; only the city list needs its own request.
  const [viewState, setViewState] = useState<State | null>(null);

  const { data: countriesData } = useCountryLookup();
  const { data, isLoading, isError, error, refetch } = useStates(page, {
    search: debouncedSearch,
    country: countryFilter || undefined,
    zone: zoneFilter || undefined,
  });
  const createMutation = useCreateState();
  const updateMutation = useUpdateState();
  const deleteMutation = useDeleteState();
  const toggleActiveMutation = useToggleStateActive();

  // Item 18: multi-select + bulk status change/delete via the Wave 0 kit.
  // `visibleIds` is this page's rows only -- selection made on another page
  // survives across pages inside the hook, but "select all" here only ever
  // means "all rows on screen" (see useBulkSelection's own doc comment).
  const visibleIds = (data?.results || []).map((state) => state.id);
  const bulkSelection = useBulkSelection(visibleIds);

  const [formData, setFormData] = useState<StateRequest>({
    country: '',
    name: '',
    code: '',
    gst_code: '',
    zone: 'B',
    is_active: true,
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [summaryMessages, setSummaryMessages] = useState<string[]>([]);

  const countryOptions = [
    { value: '', label: 'All Countries' },
    ...(countriesData?.map(c => ({ value: c.id, label: c.name })) || []),
  ];

  const handleOpenModal = (state?: State) => {
    if (state) {
      setEditingState(state);
      setFormData({
        country: state.country,
        name: state.name,
        code: state.code,
        gst_code: state.gst_code || '',
        zone: state.zone,
        is_active: state.is_active,
      });
    } else {
      setEditingState(null);
      setFormData({
        country: countryFilter || '',
        name: '',
        code: '',
        gst_code: '',
        zone: 'B',
        is_active: true,
      });
    }
    setFieldErrors({});
    setSummaryMessages([]);
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFieldErrors({});
    setSummaryMessages([]);
    try {
      if (editingState) {
        await updateMutation.mutateAsync({ id: editingState.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
      setIsModalOpen(false);
    } catch (error) {
      const nextErrors: Record<string, string> = {};
      const messages = applyServerErrors(error, (name, err) => {
        nextErrors[name as string] = err.message;
      });
      setFieldErrors(nextErrors);
      setSummaryMessages(messages);
    }
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
    setDeleteConfirm(null);
  };

  const columns = [
    {
      key: 'select',
      header: '',
      // Table's column header is plain text, so the header ("select all")
      // checkbox lives in its own strip above the table instead -- see the
      // render below. stopPropagation keeps a tick from also opening the
      // detail popup the row itself now opens on click.
      render: (state: State) => (
        <div onClick={(e) => e.stopPropagation()}>
          <BulkSelectCheckbox
            checked={bulkSelection.isSelected(state.id)}
            onChange={() => bulkSelection.toggle(state.id)}
            label={`Select ${state.name}`}
          />
        </div>
      ),
    },
    {
      key: 'name',
      header: 'State',
      render: (state: State) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-gray-100">{state.name}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Code: {state.code}</p>
        </div>
      ),
    },
    {
      key: 'country',
      header: 'Country',
      render: (state: State) => state.country_name,
    },
    {
      key: 'gst_code',
      header: 'GST Code',
      render: (state: State) => state.gst_code || '-',
    },
    {
      key: 'zone',
      header: 'Zone',
      render: (state: State) => (
        <Badge variant={zoneColors[state.zone]}>Zone {state.zone}</Badge>
      ),
    },
    {
      key: 'cities_count',
      header: 'Cities',
      render: (state: State) => state.cities_count || 0,
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (state: State) => (
        <Badge variant={state.is_active ? 'success' : 'error'}>
          {state.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (state: State) => (
        // The row itself now opens the detail popup on click; these buttons
        // must not also trigger that.
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => toggleActiveMutation.mutate(state.id)}
            className={`px-2 py-1 text-xs rounded ${
              state.is_active
                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                : 'bg-green-50 text-green-600 hover:bg-green-100'
            }`}
          >
            {state.is_active ? 'Deactivate' : 'Activate'}
          </button>
          <button
            onClick={() => handleOpenModal(state)}
            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
          >
            <PencilIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => setDeleteConfirm(state.id)}
            className="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError) {
    // A failed request must never render as "no states" -- that is a
    // different and much worse answer than "we could not load them".
    return (
      <div className="space-y-6">
        <PageHeader title="States" subtitle="Manage states and provinces" />
        <Card>
          <EmptyState
            icon={<ExclamationTriangleIcon />}
            title="Could not load states"
            description={error instanceof Error ? error.message : 'The request failed. Please try again.'}
            action={
              <button
                type="button"
                onClick={() => refetch()}
                className="text-sm font-medium text-primary-600 hover:text-primary-500"
              >
                Try again
              </button>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="States"
        subtitle="Manage states and provinces"
        actions={
          <Button onClick={() => handleOpenModal()}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Add State
          </Button>
        }
      />

      <Card>
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search states..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                options={countryOptions}
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-36">
              <Select
                options={zoneOptions}
                value={zoneFilter}
                onChange={(e) => setZoneFilter(e.target.value)}
              />
            </div>
          </div>
        </div>

        {visibleIds.length > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/60">
            <BulkSelectAllCheckbox
              checked={bulkSelection.allVisibleSelected}
              indeterminate={bulkSelection.someVisibleSelected}
              onChange={bulkSelection.toggleAllVisible}
              label="Select all states on this page"
            />
            <span className="text-sm text-gray-600 dark:text-gray-300">Select all on this page</span>
          </div>
        )}

        {bulkSelection.count > 0 && (
          <div className="px-4 pt-3">
            <BulkActionBar
              resource="geography/states"
              selectedIds={bulkSelection.selectedIds}
              entityName="states"
              onChanged={() => refetch()}
              onClear={bulkSelection.clear}
            />
          </div>
        )}

        <Table
          compact
          columns={columns}
          data={data?.results || []}
          emptyMessage="No states found"
          rowKey={(state) => state.id}
          onRowClick={(state) => setViewState(state)}
        />

        {data && data.count > 20 && (
          <div className="p-4 border-t border-gray-200 flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, data.count)} of {data.count}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={!data.next}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingState ? 'Edit State' : 'Add State'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <ErrorSummary messages={summaryMessages} />
          <Select
            label="Country"
            options={countriesData?.map(c => ({ value: c.id, label: c.name })) || []}
            value={formData.country}
            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            error={fieldErrors.country}
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="State Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              error={fieldErrors.name}
              required
            />
            <Input
              label="State Code"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              placeholder="e.g., MH"
              error={fieldErrors.code}
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="GST Code"
              value={formData.gst_code}
              onChange={(e) => setFormData({ ...formData, gst_code: e.target.value })}
              placeholder="e.g., 27"
              error={fieldErrors.gst_code}
            />
            <Select
              label="Zone"
              options={[
                { value: 'A', label: 'Zone A' },
                { value: 'B', label: 'Zone B' },
                { value: 'C', label: 'Zone C' },
              ]}
              value={formData.zone}
              onChange={(e) => setFormData({ ...formData, zone: e.target.value as 'A' | 'B' | 'C' })}
              error={fieldErrors.zone}
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded border-gray-300"
            />
            <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>
          {fieldErrors.is_active && (
            <p className="text-sm text-red-600">{fieldErrors.is_active}</p>
          )}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" type="button" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>
              {editingState ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete State"
      >
        <p className="text-gray-600 dark:text-gray-300">
          Are you sure you want to delete this state? This will also affect associated cities and pincodes.
        </p>
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            isLoading={deleteMutation.isPending}
          >
            Delete
          </Button>
        </div>
      </Modal>

      {/* Item 18: clicking a state opens its details, showing the cities
          linked to it. Keyed by id so opening a different state remounts
          the modal (and resets its own page state) instead of reusing the
          previous one's. */}
      <StateCitiesModal key={viewState?.id ?? 'none'} state={viewState} onClose={() => setViewState(null)} />
    </div>
  );
}

/**
 * Item 18's detail popup: the state's own fields plus the cities linked to
 * it. A plain "View" screen would need its own route; a popup matches what
 * the rest of this batch uses for a record's detailed view (see LANE-BRIEF).
 *
 * `state` is the row object the list already has -- no extra request for the
 * header -- but the cities table is its own paginated fetch, since a state
 * can have far more cities than are worth holding in the list row.
 */
function StateCitiesModal({ state, onClose }: { state: State | null; onClose: () => void }) {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error, refetch } = useCities(
    page,
    { state: state?.id },
    { enabled: !!state }
  );

  if (!state) return null;

  const cities = data?.results ?? [];

  return (
    <Modal isOpen onClose={onClose} title={state.name} description={`${state.country_name} · Code ${state.code}`} size="lg">
      <div className="space-y-5">
        <dl className="grid grid-cols-2 gap-4 text-sm sm:grid-cols-4">
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Zone</dt>
            <dd className="mt-1">
              <Badge variant={zoneColors[state.zone]}>Zone {state.zone}</Badge>
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">GST Code</dt>
            <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">{state.gst_code || '-'}</dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Status</dt>
            <dd className="mt-1">
              <Badge variant={state.is_active ? 'success' : 'error'}>
                {state.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </dd>
          </div>
          <div>
            <dt className="text-gray-500 dark:text-gray-400">Cities</dt>
            <dd className="mt-1 font-medium text-gray-900 dark:text-gray-100">{state.cities_count ?? cities.length}</dd>
          </div>
        </dl>

        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-2 dark:text-gray-100">Cities in {state.name}</h4>
          {isLoading ? (
            <ContentLoader />
          ) : isError ? (
            <EmptyState
              icon={<ExclamationTriangleIcon />}
              title="Could not load cities"
              description={error instanceof Error ? error.message : 'The request failed. Please try again.'}
              action={
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="text-sm font-medium text-primary-600 hover:text-primary-500"
                >
                  Try again
                </button>
              }
            />
          ) : cities.length === 0 ? (
            <EmptyState title="No cities yet" description={`${state.name} has no cities recorded.`} />
          ) : (
            <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
              {cities.map((city: City) => (
                <li key={city.id} className="flex items-center justify-between px-4 py-2 text-sm">
                  <span className="text-gray-900 dark:text-gray-100">
                    {city.name}
                    {city.code ? ` (${city.code})` : ''}
                  </span>
                  <div className="flex items-center gap-2">
                    <Badge size="sm">{city.tier.charAt(0).toUpperCase() + city.tier.slice(1)}</Badge>
                    <Badge variant={city.is_active ? 'success' : 'error'} size="sm">
                      {city.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {data && data.count > 20 && (
            <div className="mt-3 flex items-center justify-between text-sm text-gray-600 dark:text-gray-300">
              <span>
                Showing {(page - 1) * 20 + 1}-{Math.min(page * 20, data.count)} of {data.count}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={!data.next}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        <ModalFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </div>
    </Modal>
  );
}
